import { supabase } from './client'

export type AgreementType = 'owner' | 'player'

/**
 * Whether the current authenticated user has already accepted this agreement
 * version. Returns false when signed out.
 */
export async function hasAcceptedCurrent(type: AgreementType, version: string): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return false

  const { data } = await supabase
    .from('agreement_acceptances')
    .select('id')
    .eq('user_id', user.id)
    .eq('agreement_type', type)
    .eq('version', version)
    .maybeSingle()

  return Boolean(data)
}

/**
 * Record that the current authenticated user accepted an agreement version.
 *
 * Idempotent: the (user_id, agreement_type, version) unique constraint means a
 * repeat acceptance inserts nothing. Best-effort — never throws to the caller,
 * because failing to record an acceptance must not block the user's flow.
 */
export async function recordAcceptance(type: AgreementType, version: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return

  const { error } = await supabase.from('agreement_acceptances').upsert(
    {
      agreement_type: type,
      user_agent: typeof navigator === 'undefined' ? null : navigator.userAgent,
      user_id: user.id,
      version,
    },
    { ignoreDuplicates: true, onConflict: 'user_id,agreement_type,version' },
  )

  if (error) {
    // Log but swallow: acceptance recording is best-effort.
    console.error('recordAcceptance failed', error)
  }
}
